import type {
  ConversationFileDto,
  DocumentDto,
  DocumentSourceCheckDto,
  Microsoft365BrowseAt,
  Microsoft365BrowseDto,
  Microsoft365ConnectDto,
  Microsoft365Feature,
  Microsoft365ImportBody,
  Microsoft365ImportItem,
  Microsoft365ImportResultDto,
  Microsoft365ImportTarget,
  Microsoft365SavedAttachmentDto,
} from '@dbrain/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './client.js';
import { docKeys } from './documents.js';
import { microsoft365Key } from './microsoft365.js';

/**
 * Microsoft 365 part 3 for the browser (DOC-017…): browsing OneDrive, SharePoint and Outlook attachments, importing
 * up to 20 files into a place, one file into a conversation ("Bu sohbet"), saving a mail attachment from its card,
 * "Yeniden al" and the per-viewer source check. Search words and Graph ids travel in POST bodies only (never in an
 * address, so no log keeps them); the answers carry names, never a URL.
 */
export const filesKeys = {
  all: ['microsoft365-files'] as const,
  browse: (at: Microsoft365BrowseAt | null, query: string) => ['microsoft365-files', 'browse', at, query] as const,
  saved: (conversationId: string) => ['microsoft365-files', 'saved', conversationId] as const,
  check: (ids: string) => ['microsoft365-files', 'check', ids] as const,
};

/** One view of the picker, page by page ("Daha fazla göster" asks for the next with the server's sealed cursor). */
export function useMicrosoft365Browse(at: Microsoft365BrowseAt | null, query: string, enabled = true) {
  const words = query.trim();
  return useInfiniteQuery({
    queryKey: filesKeys.browse(at, words),
    queryFn: ({ pageParam }) =>
      api<Microsoft365BrowseDto>('POST', '/api/microsoft365/files/browse', { at, ...(words ? { query: words } : {}), ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.cursor ?? null,
    enabled: enabled && at !== null,
    // a folder seen a minute ago is still what Microsoft says; nothing of it is kept beyond the picker's life
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/** Library imports (Kişisel, a team, a workspace, Herkes): the documents appear at once, "İçe aktarılıyor". */
export function useMicrosoft365Import() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Microsoft365ImportBody) => api<Microsoft365ImportResultDto>('POST', '/api/microsoft365/imports', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.all }),
  });
}

/** "Bu sohbet" in a conversation: one file, copied now like an upload (the composer's chip waits for it). */
export const addConversationFile = (conversationId: string, item: Microsoft365ImportItem) =>
  api<ConversationFileDto>('POST', `/api/conversations/${conversationId}/microsoft365-files`, item);

/** "Bu sohbet" on the home page, before the conversation exists (D-93's draft, like an upload). */
export const addDraftFile = (body: { workspaceId: string; draftId?: string } & Microsoft365ImportItem) =>
  api<ConversationFileDto & { draftId: string }>('POST', '/api/uploads/microsoft365', body);

/** The attachments of this conversation saved before with "Belgelerime kaydet" (the card says so after a reload). */
export function useSavedAttachments(conversationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: filesKeys.saved(conversationId ?? ''),
    queryFn: () => api<Microsoft365SavedAttachmentDto[]>('GET', `/api/conversations/${conversationId}/microsoft365-attachments/saved`),
    enabled: enabled && Boolean(conversationId),
    staleTime: 60_000,
  });
}

/** "Belgelerime kaydet": the attachment is named by its ref in this conversation; the server finds it again. */
export function useSaveAttachment(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, target }: { ref: string; target: Microsoft365ImportTarget }) =>
      api<Microsoft365ImportResultDto>('POST', `/api/conversations/${conversationId}/microsoft365-attachments/${encodeURIComponent(ref)}/save`, { target }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: filesKeys.saved(conversationId) });
      void queryClient.invalidateQueries({ queryKey: docKeys.all });
    },
  });
}

/** "Yeniden al" with the person's own Microsoft account; the copy keeps working until the new one is ready. */
export function useRefetchDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api<DocumentDto>('POST', `/api/documents/${documentId}/refetch`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: docKeys.all }),
  });
}

/** The ids as one key, but only once they stop changing for a moment (copies finishing one by one ask once). */
function useSettled(ids: readonly string[], ms = 1500): string {
  const key = [...ids].sort().join(',');
  const [settled, setSettled] = useState(key);
  useEffect(() => {
    if (key === settled) return;
    const timer = setTimeout(() => setSettled(key), settled === '' ? 0 : ms);
    return () => clearTimeout(timer);
  }, [key, settled, ms]);
  return settled;
}

/**
 * "Kaynakta değişti": each copy on screen checked with the viewer's own account (the server keeps the answer ten
 * minutes per viewer and stores nothing). Only the copies the viewer may check are asked for (checkIdsOf).
 */
export function useSourceChecks(ids: readonly string[]): Map<string, DocumentSourceCheckDto> {
  const key = useSettled(ids);
  const query = useQuery({
    queryKey: filesKeys.check(key),
    queryFn: () => api<DocumentSourceCheckDto[]>('POST', '/api/documents/source-check', { ids: key.split(',') }),
    enabled: key !== '',
    staleTime: 10 * 60_000,
    retry: false,
  });
  return new Map((query.data ?? []).map((check) => [check.id, check]));
}

/**
 * The sign-in that asks Microsoft for a source's permission (or connects the account in the first place) and comes
 * back to `returnTo`. The client names features, never scopes; the server refuses one the firm has off.
 */
export function useMicrosoft365FeatureConnect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ returnTo, features }: { returnTo: string; features: Microsoft365Feature[] }) =>
      api<Microsoft365ConnectDto>('POST', '/api/microsoft365/connect', { returnTo, ...(features.length > 0 ? { features } : {}) }),
    onError: () => void queryClient.invalidateQueries({ queryKey: microsoft365Key }),
  });
}
